/**
 * `WelcomeStep` — ADR-0167 §1 (amended 2026-07-07). One-screen
 * orientation rendered by the Onboarding shell BEFORE step 0, only on
 * the new-account entry path (gate: `shouldShowWelcome` in
 * `lib/onboarding-preferences.ts`).
 *
 * Deliberately NOT a tour (ADR-0167 research: interruptive walkthroughs
 * don't improve task performance — NN/g). One screen: what Sill does in
 * merchant language (the identity / intent / proof trio from CLAUDE.md
 * §1) and a time expectation. NO platform question — founder decision:
 * detect-platform (+ the ADR-0052 myshopify-suffix floor) fingerprints
 * the cart automatically at the install step; never ask what we can
 * detect. "Get started" marks the browser-local `welcomed` flag and
 * falls through to the untouched 4-step machine.
 *
 * Claims here follow the verified-facts discipline: identification,
 * per-skill control, and signed audit records are shipped Phase-1
 * surfaces; "free, no card required" matches the live marketing CTA.
 */

import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';


import { useC } from '../../lib/theme.js';
import { markWelcomed } from '../../lib/onboarding-preferences.js';
import { Card } from '../../components/atoms/index.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";
const serifFont = "'Instrument Serif', serif";

const TRIO: ReadonlyArray<{ kicker: string; body: string }> = [
  { kicker: 'IDENTITY', body: 'Know every AI agent that visits your store.' },
  { kicker: 'INTENT', body: 'Decide what each agent is allowed to do.' },
  { kicker: 'PROOF', body: 'Keep a signed record of every interaction.' },
];

export interface WelcomeStepProps {
  /** Fires after the flag write; the shell falls through to step 0. */
  onContinue: () => void;
}

export function WelcomeStep({ onContinue }: WelcomeStepProps): JSX.Element {
  const C = useC();

  const continueToFlow = (): void => {
    markWelcomed();
    onContinue();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      data-testid="onboarding-welcome"
    >
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          color: C.muted,
          letterSpacing: 2,
          marginBottom: 12,
        }}
      >
        WELCOME TO SILL
      </div>
      <h1
        style={{
          fontFamily: serifFont,
          fontSize: 'clamp(36px, 6vw, 56px)',
          lineHeight: 1.05,
          color: C.text,
          letterSpacing: -1.5,
          marginBottom: 16,
        }}
      >
        Get your store ready for <em>AI agents</em>.
      </h1>
      <p
        style={{
          fontFamily: sansFont,
          fontSize: 'clamp(14px, 1.6vw, 16px)',
          color: C.muted,
          lineHeight: 1.6,
          maxWidth: 640,
          marginBottom: 24,
        }}
      >
        Sill sits between visiting AI agents and your store. It identifies
        every agent, controls what each one can do, and keeps a signed
        record of every interaction. Setup takes about five minutes.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
        {TRIO.map((t) => (
          <Card key={t.kicker} className="p-4">
            <div
              style={{
                fontFamily: monoFont,
                fontSize: 10,
                color: C.limeFg,
                letterSpacing: 2,
                marginBottom: 6,
              }}
            >
              {t.kicker}
            </div>
            <div
              style={{
                fontFamily: sansFont,
                fontSize: 13,
                color: C.text,
                lineHeight: 1.5,
              }}
            >
              {t.body}
            </div>
          </Card>
        ))}
      </div>

      <p
        style={{
          fontFamily: sansFont,
          fontSize: 13,
          color: C.muted,
          lineHeight: 1.55,
          marginBottom: 24,
          maxWidth: 640,
        }}
      >
        Add your domain and Sill detects your platform — Shopify,
        WordPress, Cloudflare, or a custom cart — and tailors the
        install for it.
      </p>

      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <button
          type="button"
          onClick={continueToFlow}
          className="flex items-center justify-center gap-2 px-6 py-2.5"
          style={{
            background: C.lime,
            color: C.ink,
            fontFamily: sansFont,
            fontWeight: 600,
            fontSize: 14,
            borderRadius: 2,
          }}
          data-testid="welcome-continue"
        >
          Get started <ArrowRight size={15} />
        </button>
        <span
          style={{
            fontFamily: sansFont,
            fontSize: 12,
            color: C.muted,
          }}
        >
          Discovery is free. No card required.
        </span>
      </div>
    </motion.div>
  );
}
